import React, { useEffect, useState } from "react";
import { Dialog, IconButton, CircularProgress } from "@mui/material";
import { Close, Groups, Campaign } from "@mui/icons-material";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { toast } from "react-toastify";

const STATUS_CONFIG = {
  pending: { bg: "#fffbeb", color: "#d97706", label: "Pending" },
  approved: { bg: "#ecfdf5", color: "#059669", label: "Approved" },
  declined: { bg: "#fef2f2", color: "#dc2626", label: "Declined" },
};

const Field = ({ label, value, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <p className="text-xs text-gray-400 font-abel uppercase tracking-wider">{label}</p>
    <p className="text-sm text-gray-700 font-abel font-semibold mt-0.5">{value || "—"}</p>
  </div>
);

export const MemberPreviewModal = ({ open, onClose, member, familyName, familyLoading, canViewPhone, canMakeAuthor }) => {
  const [makingAuthor, setMakingAuthor] = useState(false);
  const [authorCategory, setAuthorCategory] = useState("Evangelist");
  const [customAuthorCategory, setCustomAuthorCategory] = useState("");
  const [authorError, setAuthorError] = useState("");
  const [authorLoading, setAuthorLoading] = useState(false);
  const [checkingAuthor, setCheckingAuthor] = useState(false);

  useEffect(() => {
    setMakingAuthor(false);
    setAuthorCategory("Evangelist");
    setCustomAuthorCategory("");
    setAuthorError("");
  }, [member, open]);

  if (!member) return null;

  const isAlreadyAuthor = async () => {
    const checks = [];
    if (member.email) checks.push(getDocs(query(collection(db, "authors"), where("email", "==", member.email))));
    if (member.phone) checks.push(getDocs(query(collection(db, "authors"), where("phone", "==", member.phone))));
    if (!checks.length) return false;
    const results = await Promise.all(checks);
    return results.some((snap) => !snap.empty);
  };

  const handleOpenMakeAuthor = async () => {
    setCheckingAuthor(true);
    try {
      if (await isAlreadyAuthor()) {
        toast(`${member.name} is already an Author`, { type: "info" });
        return;
      }
      setMakingAuthor(true);
    } finally {
      setCheckingAuthor(false);
    }
  };

  const handleMakeAuthor = async () => {
    if (authorCategory === "Other" && !customAuthorCategory.trim()) {
      setAuthorError("Please specify a category");
      return;
    }

    setAuthorLoading(true);
    setAuthorError("");
    try {
      if (await isAlreadyAuthor()) {
        setAuthorError("This person is already an Author");
        setAuthorLoading(false);
        return;
      }

      const finalCategory = authorCategory === "Other" ? customAuthorCategory.trim() : authorCategory;

      await addDoc(collection(db, "authors"), {
        name: member.name,
        email: member.email || "",
        phone: member.phone || "",
        category: finalCategory,
        description: "",
        imgSrc: member.imgSrc || "",
        imgId: "",
        status: "approved",
        createdAt: serverTimestamp(),
      });

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Made ${member.name} an Author`,
        createdAt: serverTimestamp(),
      });

      toast(`${member.name} is now an Author!`, { type: "success" });
      setMakingAuthor(false);
      onClose();
    } catch (err) {
      setAuthorError(err.message || "Failed to create author");
    } finally {
      setAuthorLoading(false);
    }
  };

  const statusCfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.pending;
  const department = Array.isArray(member.department)
    ? member.department.join(", ")
    : member.department;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <div className="relative p-6">
        <IconButton
          onClick={onClose}
          size="small"
          className="!absolute !top-3 !right-3"
          sx={{ color: "#9ca3af" }}
        >
          <Close fontSize="small" />
        </IconButton>

        <div className="flex flex-col items-center text-center">
          {member.imgSrc ? (
            <img
              src={member.imgSrc}
              alt={member.name}
              className="w-24 h-24 rounded-full object-cover border-2 border-[#000C66]/10 mb-3"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#eef2ff] flex items-center justify-center mb-3 border-2 border-gray-100">
              <span className="text-2xl font-bold text-[#000C66] font-aref">
                {member.name?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
          )}

          <h2 className="text-lg font-bold text-[#000C66] font-aref">{member.name}</h2>

          <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
            <span
              className="text-xs font-abel font-semibold px-2.5 py-1 rounded-full"
              style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}
            >
              {statusCfg.label}
            </span>
            {familyLoading ? (
              <CircularProgress size={12} sx={{ color: "#be185d" }} />
            ) : familyName ? (
              <span className="inline-flex items-center gap-1 text-xs font-abel font-semibold px-2.5 py-1 bg-[#fdf2f8] text-[#be185d] rounded-full">
                <Groups style={{ fontSize: 13 }} /> {familyName} Family
              </span>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <Field label="Gender" value={member.gender} />
          <Field label="Date of Birth" value={member.dob} />
          {canViewPhone && <Field label="Phone" value={member.phone} />}
          <Field label="Email" value={member.email} />
          <Field label="Address" value={member.address} wide />
          <Field label="Department" value={department} wide />
          <Field label="Favourite Hymn" value={member.favHymns} />
          <Field label="Favourite Bible Verse" value={member.favBibleVerse} />
        </div>

        {canMakeAuthor && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            {!makingAuthor ? (
              <button
                onClick={handleOpenMakeAuthor}
                disabled={checkingAuthor}
                className="w-full flex items-center justify-center gap-2 bg-[#000C66] text-white px-4 py-2.5 rounded-xl font-abel font-semibold text-sm hover:bg-blue-900 transition disabled:opacity-60"
              >
                {checkingAuthor ? (
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Campaign style={{ fontSize: 16 }} />
                )}
                Make Author
              </button>
            ) : (
              <div className="space-y-3">
                {authorError && (
                  <p className="text-xs text-red-600 font-abel bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {authorError}
                  </p>
                )}
                <div>
                  <label className="text-xs text-gray-400 font-abel uppercase tracking-wider">Category *</label>
                  <select
                    value={authorCategory}
                    onChange={(e) => { setAuthorCategory(e.target.value); setAuthorError(""); }}
                    className="w-full mt-1 px-4 py-2.5 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-white"
                  >
                    <option value="Evangelist">Evangelist</option>
                    <option value="Prophet">Prophet</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {authorCategory === "Other" && (
                  <input
                    type="text"
                    value={customAuthorCategory}
                    onChange={(e) => { setCustomAuthorCategory(e.target.value); setAuthorError(""); }}
                    placeholder="Specify category…"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                  />
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setMakingAuthor(false)}
                    disabled={authorLoading}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-abel font-medium hover:bg-gray-50 transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMakeAuthor}
                    disabled={authorLoading}
                    className="flex-1 py-2.5 rounded-xl bg-[#000C66] text-white font-abel font-semibold hover:bg-blue-900 transition text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {authorLoading && (
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )}
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
